import React, { useMemo } from 'react';
import {
  Box,
  Text,
  VStack,
  HStack,
  Icon,
  Flex,
  Badge,
  keyframes,
  useColorModeValue,
} from '@chakra-ui/react';
import {
  AreaChart,
  Area,
  ResponsiveContainer,
  Cell,
  XAxis,
  YAxis,
  Tooltip,
  ScatterChart,
  Scatter,
  ZAxis,
  PieChart,
  Pie,
} from 'recharts';
import { motion } from 'framer-motion';
import {
  FaArrowUp,
  FaArrowDown,
  FaFire,
  FaCrown,
  FaBolt,
} from 'react-icons/fa';
import { formatNumber } from '../utils/competitiveIntelHelpers';

const MotionBox = motion(Box);

// Color palette for the visualizations
const COLORS = {
  emerald: {
    gradient: ['#10B981', '#34D399', '#6EE7B7'],
    light: 'rgba(16, 185, 129, 0.1)',
    main: '#10B981',
    dark: '#059669',
  },
  indigo: {
    gradient: ['#6366F1', '#818CF8', '#A5B4FC'],
    light: 'rgba(99, 102, 241, 0.1)',
    main: '#6366F1',
    dark: '#4F46E5',
  },
  amber: {
    gradient: ['#F59E0B', '#FBBF24', '#FCD34D'],
    light: 'rgba(245, 158, 11, 0.1)',
    main: '#F59E0B',
    dark: '#D97706',
  },
  rose: {
    gradient: ['#F43F5E', '#FB7185', '#FDA4AF'],
    light: 'rgba(244, 63, 94, 0.1)',
    main: '#F43F5E',
    dark: '#E11D48',
  },
  cyan: {
    gradient: ['#06B6D4', '#22D3EE', '#67E8F9'],
    light: 'rgba(6, 182, 212, 0.1)',
    main: '#06B6D4',
    dark: '#0891B2',
  },
  purple: {
    gradient: ['#8B5CF6', '#A78BFA', '#C4B5FD'],
    light: 'rgba(139, 92, 246, 0.1)',
    main: '#8B5CF6',
    dark: '#7C3AED',
  },
};

const formatSignedNumber = (value) => {
  const safeValue = Number(value) || 0;
  const formatted = formatNumber(Math.abs(safeValue));
  if (safeValue === 0) return '0';
  return safeValue < 0 ? `-${formatted}` : formatted;
};

const formatSignedChange = (value) => {
  const safeValue = Number(value) || 0;
  const formatted = formatNumber(Math.abs(safeValue));
  if (safeValue === 0) return '0';
  return safeValue > 0 ? `+${formatted}` : `-${formatted}`;
};

// Shimmer animation for heat gauge
const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

// ============================================================================
// 1. MOMENTUM SPARKLINE - Mini trend lines for cards
// ============================================================================

/**
 * MomentumSparkline - A sleek mini area chart for showing trends
 * 
 * @param {Array} data - Array of { value } objects
 * @param {boolean} isPositive - Whether the trend is positive
 * @param {string} color - Color scheme: 'emerald', 'indigo', 'amber', etc.
 * @param {number} width - Width of the sparkline
 * @param {number} height - Height of the sparkline
 */
export const MomentumSparkline = ({ 
  data = [], 
  isPositive = true, 
  color = 'emerald',
  width = 80,
  height = 40,
}) => {
  const colorScheme = COLORS[color] || COLORS.emerald;
  
  // Transform data if needed
  const chartData = useMemo(() => {
    if (!data || data.length === 0) {
      // Generate dummy upward/downward trend
      return isPositive 
        ? [{ value: 20 }, { value: 25 }, { value: 30 }, { value: 35 }, { value: 50 }, { value: 65 }]
        : [{ value: 65 }, { value: 50 }, { value: 45 }, { value: 35 }, { value: 30 }, { value: 20 }];
    }
    return data.map((d, i) => ({ value: typeof d === 'object' ? d.value : d, index: i }));
  }, [data, isPositive]);

  const gradientId = `sparkline-gradient-${Math.random().toString(36).substr(2, 9)}`;

  return (
    <Box width={`${width}px`} height={`${height}px`} position="relative">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={chartData} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={colorScheme.main} stopOpacity={0.4} />
              <stop offset="100%" stopColor={colorScheme.main} stopOpacity={0.05} />
            </linearGradient>
          </defs>
          <Area
            type="monotone"
            dataKey="value"
            stroke={colorScheme.main}
            strokeWidth={1.5}
            fill={`url(#${gradientId})`}
            dot={false}
            animationDuration={1000}
            animationBegin={200}
          />
        </AreaChart>
      </ResponsiveContainer>
    </Box>
  );
};

// ============================================================================
// 2. VELOCITY COMPARISON BAR CHART - Glassmorphism horizontal bars
// ============================================================================

/**
 * VelocityBarChart - Horizontal bar chart with glassmorphism styling
 * 
 * @param {Array} data - Array of { name, value, growth } objects
 * @param {Function} onBarClick - Callback when a bar is clicked
 * @param {string} metricLabel - Label for the metric (e.g., "reviews")
 */
export const VelocityBarChart = ({ 
  data = [], 
  onBarClick,
  metricLabel = 'reviews',
}) => {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');
  
  // Sort data by value descending
  const sortedData = useMemo(() => {
    return [...data]
      .sort((a, b) => b.value - a.value)
      .slice(0, 7); // Top 7
  }, [data]);

  const maxValue = sortedData[0]?.value || 1;

  // Color for each bar - top gets gold
  const getBarColor = (index, value) => {
    if (index === 0) return COLORS.amber.main; // Gold for leader
    if (index === 1) return COLORS.indigo.main;
    if (index === 2) return COLORS.purple.main;
    return COLORS.cyan.main;
  };

  return (
    <MotionBox
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <VStack spacing={3} align="stretch">
        {sortedData.map((item, index) => {
          const percentage = (item.value / maxValue) * 100;
          const barColor = getBarColor(index, item.value);
          
          return (
            <MotionBox
              key={item.name}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.4, delay: index * 0.08 }}
            >
              <Box
                p={3}
                bg={cardBg}
                borderRadius="xl"
                borderWidth="1px"
                borderColor={borderColor}
                position="relative"
                overflow="hidden"
                cursor={onBarClick ? 'pointer' : 'default'}
                onClick={() => onBarClick?.(item)}
                transition="all 0.3s"
                _hover={{
                  transform: 'translateX(4px)',
                  boxShadow: `0 4px 20px ${barColor}30`,
                  borderColor: barColor,
                }}
              >
                {/* Glassmorphism background bar */}
                <Box
                  position="absolute"
                  left={0}
                  top={0}
                  height="100%"
                  borderRadius="xl"
                  sx={{
                    width: `${percentage}%`,
                    background: `linear-gradient(90deg, ${barColor}15, ${barColor}08)`,
                    transition: 'width 1s ease-out',
                    borderRight: `2px solid ${barColor}60`,
                    boxShadow: `inset 0 0 20px ${barColor}10`,
                  }}
                />

                <Flex position="relative" justify="space-between" align="center">
                  {/* Left: Rank and Name */}
                  <HStack spacing={3} flex={1} minW={0}>
                    {index === 0 && (
                      <Icon as={FaCrown} color="yellow.400" boxSize={4} />
                    )}
                    <Text
                      fontSize="13px"
                      fontWeight={index === 0 ? '700' : '600'}
                      color={index === 0 ? 'gray.900' : 'gray.700'}
                      noOfLines={1}
                    >
                      {item.name}
                    </Text>
                  </HStack>

                  {/* Right: Value and Growth Badge */}
                  <HStack spacing={2}>
                    <Text
                      fontSize="18px"
                      fontWeight="700"
                      color={barColor}
                    >
                      {item.value}
                    </Text>
                    {item.growth !== undefined && (
                      <Badge
                        px={2}
                        py={0.5}
                        borderRadius="full"
                        fontSize="xs"
                        fontWeight="bold"
                        bg={item.growth >= 0 ? COLORS.emerald.light : COLORS.rose.light}
                        color={item.growth >= 0 ? COLORS.emerald.dark : COLORS.rose.dark}
                        display="flex"
                        alignItems="center"
                      >
                        <Icon
                          as={item.growth >= 0 ? FaArrowUp : FaArrowDown}
                          boxSize={2}
                          mr={1}
                        />
                        {item.growth >= 0 ? '+' : ''}{item.growth}
                      </Badge>
                    )}
                  </HStack>
                </Flex>
              </Box>
            </MotionBox>
          );
        })}
      </VStack>
    </MotionBox>
  );
};

// ============================================================================
// 3. MARKET MOMENTUM MAP (BUBBLE CHART) - Gross growth vs gross acceleration
// ============================================================================

/**
 * Custom tooltip for the momentum bubble chart
 */
const BubbleTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  
  const data = payload[0]?.payload;
  if (!data) return null;

  const accelerationColor = data.grossAcceleration > 0
    ? 'emerald.600'
    : data.grossAcceleration < 0
      ? 'rose.600'
      : 'gray.600';

  return (
    <Box
      bg="white"
      borderRadius="lg"
      boxShadow="lg"
      p={4}
      border="1px solid"
      borderColor="gray.200"
      maxW="240px"
    >
      <Text fontWeight="bold" fontSize="sm" mb={2} color="gray.800">
        {data.name}
      </Text>
      <VStack spacing={1} align="stretch">
        <HStack justify="space-between">
          <Text fontSize="xs" color="gray.500">Gross Growth</Text>
          <Text fontSize="xs" fontWeight="bold" color="indigo.600">
            {formatNumber(data.grossGrowth)}
          </Text>
        </HStack>
        <HStack justify="space-between">
          <Text fontSize="xs" color="gray.500">Gross Acceleration</Text>
          <Text fontSize="xs" fontWeight="bold" color={accelerationColor}>
            {formatSignedChange(data.grossAcceleration)}
          </Text>
        </HStack>
        <HStack justify="space-between">
          <Text fontSize="xs" color="gray.500">Total Reviews</Text>
          <Text fontSize="xs" fontWeight="bold" color="blue.600">
            {formatNumber(data.totalReviews)}
          </Text>
        </HStack>
        {data.isAscending && (
          <HStack spacing={1} mt={1}>
            <Icon as={FaBolt} color="yellow.500" boxSize={3} />
            <Text fontSize="xs" color="yellow.600" fontWeight="medium">
              Accelerating Business
            </Text>
          </HStack>
        )}
      </VStack>
    </Box>
  );
};

/**
 * MarketMomentumMap - Bubble chart showing gross growth vs gross acceleration
 *
 * @param {Array} data - Array of { name, grossGrowth, grossAcceleration, totalReviews }
 * @param {Function} onBubbleClick - Callback when a bubble is clicked
 * @param {number} height - Chart height
 */
export const MarketMomentumMap = ({ 
  data = [], 
  onBubbleClick,
  height = 400,
}) => {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const gridColor = useColorModeValue('#E5E7EB', '#374151');

  // Process data for the scatter plot
  const chartData = useMemo(() => {
    if (!data || data.length === 0) return [];
    
    // Filter out businesses without required data
    const validData = data.filter(d => 
      d.grossGrowth > 0 || d.grossAcceleration !== 0
    );

    // Calculate min/max for bubble sizing
    const maxReviewBase = Math.max(...validData.map(d => d.totalReviews || 0), 1);
    
    return validData.map(d => ({
      ...d,
      x: d.grossGrowth || 0,
      y: d.grossAcceleration || 0,
      z: Math.max((d.totalReviews || 0) / maxReviewBase * 100, 10), // Normalized size
      // Mark accelerating businesses
      isAscending: d.grossAcceleration > 0 && d.grossGrowth > 0,
    }));
  }, [data]);

  if (chartData.length === 0) {
    return (
      <Box p={6} textAlign="center" color="gray.500">
        <Text>No data available for the momentum map</Text>
      </Box>
    );
  }

  return (
    <MotionBox
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.6 }}
      bg={cardBg}
      borderRadius="xl"
      borderWidth="1px"
      borderColor={borderColor}
      p={6}
      boxShadow="sm"
    >
      <VStack align="stretch" spacing={4}>
        <HStack justify="space-between">
          <Box>
            <Text fontSize="lg" fontWeight="700" color="gray.900">
              Market Momentum Map
            </Text>
            <Text fontSize="sm" color="gray.500">
              Gross growth vs. gross acceleration • Bubble size = review base
            </Text>
          </Box>
          <HStack spacing={4} fontSize="xs" color="gray.500">
            <HStack spacing={1}>
              <Box w={3} h={3} borderRadius="full" bg={COLORS.indigo.main} />
              <Text>Normal</Text>
            </HStack>
            <HStack spacing={1}>
              <Box w={3} h={3} borderRadius="full" bg={COLORS.emerald.main} />
              <Text>Accelerating</Text>
            </HStack>
          </HStack>
        </HStack>

        <Box height={`${height}px`}>
          <ResponsiveContainer width="100%" height="100%">
            <ScatterChart margin={{ top: 20, right: 20, bottom: 40, left: 60 }}>
              <defs>
                <filter id="glow">
                  <feGaussianBlur stdDeviation="3" result="coloredBlur" />
                  <feMerge>
                    <feMergeNode in="coloredBlur" />
                    <feMergeNode in="SourceGraphic" />
                  </feMerge>
                </filter>
              </defs>
              <XAxis
                type="number"
                dataKey="x"
                name="Gross Growth"
                domain={[0, 'dataMax']}
                tickLine={false}
                axisLine={{ stroke: gridColor }}
                tick={{ fill: '#4A5568', fontSize: 13, fontWeight: 600 }}
                tickFormatter={formatNumber}
                label={{
                  value: 'Gross Growth (reviews)',
                  position: 'bottom',
                  fill: '#6B7280',
                  fontSize: 12,
                }}
              />
              <YAxis
                type="number"
                dataKey="y"
                name="Gross Acceleration"
                tickLine={false}
                axisLine={{ stroke: gridColor }}
                tick={{ fill: '#4A5568', fontSize: 13, fontWeight: 600 }}
                tickFormatter={formatSignedNumber}
                domain={['dataMin', 'dataMax']}
                label={{
                  value: 'Gross Acceleration (Δ reviews)',
                  angle: -90,
                  position: 'insideLeft',
                  fill: '#6B7280',
                  fontSize: 12,
                }}
              />
              <ZAxis type="number" dataKey="z" range={[100, 600]} />
              <Tooltip content={<BubbleTooltip />} />
              <Scatter
                data={chartData}
                onClick={(data) => onBubbleClick?.(data.payload)}
              >
                {chartData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={entry.isAscending ? COLORS.emerald.main : COLORS.indigo.main}
                    fillOpacity={0.7}
                    stroke={entry.isAscending ? COLORS.emerald.dark : COLORS.indigo.dark}
                    strokeWidth={2}
                    style={{
                      cursor: 'pointer',
                      filter: entry.isAscending ? 'url(#glow)' : 'none',
                    }}
                  />
                ))}
              </Scatter>
            </ScatterChart>
          </ResponsiveContainer>
        </Box>
      </VStack>
    </MotionBox>
  );
};

// ============================================================================
// 4. MARKET HEAT INDEX (RADIAL GAUGE) - Demand Score Visualization
// ============================================================================

/**
 * MarketHeatGauge - Semi-circle gauge showing month-over-month comparison
 * Shows whether last month was BUSIER, SLOWER, or STEADY compared to the month before
 * 
 * @param {number} score - The gauge score (0-100), where 50 = no change
 * @param {number} change - Percentage change from previous month
 * @param {string} label - Label for the widget
 */
export const MarketHeatGauge = ({
  score = 50,
  change = 0,
  label = 'Market Demand',
}) => {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  
  // Clamp score between 0 and 100
  const normalizedScore = Math.min(Math.max(score, 0), 100);
  
  // Calculate the angle for the needle (0 score = -90deg, 100 score = 90deg)
  const needleAngle = -90 + (normalizedScore / 100) * 180;
  
  // Determine comparison label and color based on percentage change
  const getComparisonInfo = (percentChange) => {
    if (percentChange > 5) return { main: COLORS.emerald.main, label: 'Busier', bg: COLORS.emerald.light };
    if (percentChange < -5) return { main: COLORS.rose.main, label: 'Slower', bg: COLORS.rose.light };
    return { main: COLORS.amber.main, label: 'Steady', bg: COLORS.amber.light };
  };
  
  const scoreInfo = getComparisonInfo(change);
  
  // Create gauge data for the pie chart
  const gaugeData = [
    { name: 'Low', value: 20, fill: COLORS.rose.main },
    { name: 'Slow', value: 20, fill: COLORS.amber.dark },
    { name: 'Moderate', value: 20, fill: COLORS.amber.main },
    { name: 'Healthy', value: 20, fill: COLORS.cyan.main },
    { name: 'Surging', value: 20, fill: COLORS.emerald.main },
  ];

  return (
    <MotionBox
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      bg={cardBg}
      borderRadius="2xl"
      borderWidth="1px"
      borderColor={borderColor}
      p={6}
      boxShadow="sm"
      position="relative"
      overflow="hidden"
    >
      {/* Shimmer effect for high scores */}
      {normalizedScore >= 70 && (
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          height="4px"
          sx={{
            background: `linear-gradient(90deg, transparent, ${scoreInfo.main}50, transparent)`,
            backgroundSize: '200% 100%',
            animation: `${shimmer} 2s infinite linear`,
          }}
        />
      )}

      <VStack spacing={2}>
        {/* Title */}
        <Text fontSize="lg" fontWeight="700" color="gray.800">
          {label}
        </Text>

        {/* Gauge Container */}
        <Box position="relative" height="180px" width="280px">
          {/* The gauge arc */}
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <defs>
                {Object.entries(COLORS).map(([key, value]) => (
                  <linearGradient key={key} id={`gauge-${key}`} x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor={value.gradient[0]} />
                    <stop offset="100%" stopColor={value.gradient[2]} />
                  </linearGradient>
                ))}
              </defs>
              <Pie
                data={gaugeData}
                cx="50%"
                cy="100%"
                startAngle={180}
                endAngle={0}
                innerRadius={80}
                outerRadius={110}
                paddingAngle={2}
                dataKey="value"
                stroke="none"
              >
                {gaugeData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.fill} opacity={0.8} />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>

          {/* Needle */}
          <Box
            position="absolute"
            bottom="0"
            left="50%"
            transformOrigin="bottom center"
            sx={{
              transform: `translateX(-50%) rotate(${needleAngle}deg)`,
              transition: 'transform 1s cubic-bezier(0.34, 1.56, 0.64, 1)',
            }}
          >
            <Box
              width="4px"
              height="70px"
              bg={`linear-gradient(to top, ${scoreInfo.main}, transparent)`}
              borderRadius="full"
            />
          </Box>

        </Box>

        {/* Status Label - BUSIER/SLOWER/STEADY */}
        <Text
          fontSize="xl"
          fontWeight="bold"
          color={scoreInfo.main}
          textTransform="uppercase"
        >
          {scoreInfo.label}
        </Text>
          
        {/* Change indicator */}
        {change !== 0 && (
          <HStack spacing={1}>
            <Icon
              as={change >= 0 ? FaArrowUp : FaArrowDown}
              color={change >= 0 ? 'green.500' : 'red.500'}
              boxSize={3}
            />
            <Text
              fontSize="sm"
              fontWeight="bold"
              color={change >= 0 ? 'green.600' : 'red.600'}
            >
              {change >= 0 ? '+' : ''}{change.toFixed(0)}% vs previous month
            </Text>
          </HStack>
        )}
      </VStack>
    </MotionBox>
  );
};

// ============================================================================
// 5. ENHANCED CHANGE CARD WITH SPARKLINE
// ============================================================================

/**
 * EnhancedChangeCard - Card component with sparkline visualization
 */
export const EnhancedChangeCard = ({
  business,
  metricLabel,
  metricValue,
  subValue,
  icon,
  colorScheme = 'blue',
  trend,
  sparklineData = [],
  onClick,
}) => {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');
  
  const colors = COLORS[colorScheme] || COLORS.indigo;
  const isPositive = trend === 'up';

  return (
    <MotionBox
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -4, boxShadow: `0 8px 30px ${colors.main}20` }}
      transition={{ duration: 0.3 }}
    >
      <Box
        bg={cardBg}
        borderRadius="xl"
        borderWidth="1px"
        borderColor={borderColor}
        p={4}
        cursor={onClick ? 'pointer' : 'default'}
        onClick={() => onClick?.(business)}
        transition="all 0.2s"
        _hover={{ borderColor: colors.main }}
      >
        <VStack spacing={3} align="stretch">
          {/* Header */}
          <HStack justify="space-between">
            <Text
              fontWeight="bold"
              fontSize="sm"
              noOfLines={1}
              flex={1}
              color="gray.800"
            >
              {business.name}
            </Text>
            <Box bg={`${colorScheme}.50`} p={1.5} borderRadius="md">
              <Icon as={icon} color={colors.main} boxSize={3.5} />
            </Box>
          </HStack>

          {/* Metric and Sparkline */}
          <HStack justify="space-between" align="flex-end">
            <VStack spacing={0} align="flex-start">
              <Text fontSize="xs" color="gray.500">{metricLabel}</Text>
              <Text fontSize="2xl" fontWeight="bold" color={colors.main} lineHeight="1">
                {metricValue}
              </Text>
              {subValue && (
                <HStack spacing={1} mt={1}>
                  {trend && (
                    <Icon
                      as={isPositive ? FaArrowUp : FaArrowDown}
                      color={isPositive ? 'green.500' : 'red.500'}
                      boxSize={2.5}
                    />
                  )}
                  <Text fontSize="xs" color={isPositive ? 'green.600' : 'red.600'}>
                    {subValue}
                  </Text>
                </HStack>
              )}
            </VStack>

            {/* Sparkline */}
            <MomentumSparkline
              data={sparklineData}
              isPositive={isPositive}
              color={colorScheme === 'blue' ? 'indigo' : colorScheme === 'green' ? 'emerald' : colorScheme}
              width={70}
              height={35}
            />
          </HStack>
        </VStack>
      </Box>
    </MotionBox>
  );
};

const MarketDataVisualizations = {
  MomentumSparkline,
  VelocityBarChart,
  MarketMomentumMap,
  MarketHeatGauge,
  EnhancedChangeCard,
};

export default MarketDataVisualizations;
